import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  fetchWalletUser,
  getCsMemos,
  addCsMemo,
  setRiskFlag,
} from "../modules/wallet/services/walletService";
import ManualChargeModal from "../modules/wallet/components/ManualChargeModal";
import { useAccess } from "../context/AccessContext";
import "../css/WalletUserDetail.css";
import Layout from "./Layout";

const LEDGER_TYPE_LABEL = {
  CHARGE: '충전',
  MANUAL_CHARGE: '수기 충전',
  PAYMENT: '결제 차감',
  REFUND: '취소 원복',
  WITHDRAWAL: '출금',
  WITHDRAWAL_REJECT: '출금 거절',
  REFERRAL: '레퍼럴',
  REFERRAL_DEBT: '레퍼럴 부채',
  ADJUST: '조정',
};

function formatNumber(n) {
  if (n == null) return "-";
  return Number(n).toLocaleString();
}

function formatDateTime(str) {
  if (!str) return "-";
  return str.replace("T", " ").slice(0, 19);
}

const RISK_STATUS_LABELS = {
  NORMAL: "정상",
  CAUTION: "주의",
  RESTRICTED: "제한",
};

function RiskBadge({ status }) {
  const label = RISK_STATUS_LABELS[status] || status || "-";
  return (
    <span className={`wud-risk-badge ${status || ""}`}>{label}</span>
  );
}

function WalletUserDetail() {
  const navigate = useNavigate();
  const { userId: paramUserId } = useParams();
  const location = useLocation();
  const prefetched = location.state?.prefetched || null;
  const { hasPermission } = useAccess();
  const mayManualCharge = hasPermission("wallet.user_charge.execute"); // 수기 충전
  const mayUpdateRiskFlag = hasPermission("wallet.user_risk_flag.update"); // 위험 상태 변경

  const [searchId, setSearchId] = useState("");
  const [targetUserId, setTargetUserId] = useState(paramUserId || null);
  const [user, setUser] = useState(null);
  const [memos, setMemos] = useState([]);
  const [loadingUser, setLoadingUser] = useState(false);
  const [memoContent, setMemoContent] = useState("");
  const [memoSubmitting, setMemoSubmitting] = useState(false);
  const [riskStatus, setRiskStatus] = useState("");
  const [riskReason, setRiskReason] = useState("");
  const [riskSubmitting, setRiskSubmitting] = useState(false);
  const [showChargeModal, setShowChargeModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    if (prefetched && !refreshKey) {
      setUser(prefetched);
      if (prefetched.riskStatus) setRiskStatus(prefetched.riskStatus);
      getCsMemos(targetUserId)
        .then((data) => setMemos(data?.memos || []))
        .catch(() => setMemos(prefetched.recentMemos || []));
      return;
    }
    if (!targetUserId) return;

    let cancelled = false;
    async function load() {
      setLoadingUser(true);
      setError("");
      try {
        const [userData, memosData] = await Promise.all([
          fetchWalletUser(targetUserId),
          getCsMemos(targetUserId),
        ]);
        if (!cancelled) {
          setUser(userData);
          setMemos(memosData?.memos || []);
          if (userData?.riskStatus) {
            setRiskStatus(userData.riskStatus);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.response?.data?.detail || "사용자 정보를 불러오지 못했습니다."
          );
          setUser(null);
          setMemos([]);
        }
      } finally {
        if (!cancelled) setLoadingUser(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [targetUserId, refreshKey]);

  function handleSearch() {
    const id = searchId.trim();
    if (!id) return;
    setTargetUserId(id);
  }

  function handleSearchKeyDown(e) {
    if (e.key === "Enter") handleSearch();
  }

  async function handleMemoSubmit() {
    if (!memoContent.trim() || !targetUserId) return;
    setMemoSubmitting(true);
    try {
      await addCsMemo(targetUserId, memoContent.trim());
      setMemoContent("");
      const updated = await getCsMemos(targetUserId);
      setMemos(updated?.memos || []);
    } catch (err) {
      alert(err?.response?.data?.detail || "메모 등록에 실패했습니다.");
    } finally {
      setMemoSubmitting(false);
    }
  }

  async function handleRiskSubmit() {
    if (!riskStatus || !riskReason.trim() || !targetUserId) return;
    setRiskSubmitting(true);
    try {
      await setRiskFlag(targetUserId, riskStatus, riskReason.trim());
      setRiskReason("");
      setRefreshKey((k) => k + 1);
    } catch (err) {
      alert(err?.response?.data?.detail || "위험 상태 변경에 실패했습니다.");
    } finally {
      setRiskSubmitting(false);
    }
  }

  const ledger = user?.recentLedgers || [];

  const kpiCards = [
    { label: "총 지갑 잔액", value: `${formatNumber(user?.totalBalance)} P`, color: "#6366f1" },
    { label: "CP 잔액",      value: `${formatNumber(user?.cpBalance)} P`,    color: "#0ea5e9" },
    { label: "EP 잔액",      value: `${formatNumber(user?.epBalance)} P`,    color: "#10b981" },
    { label: "출금 예약 CP", value: `${formatNumber(user?.reservedCp)} P`,  color: "#f59e0b" },
    { label: "출금 가능 CP", value: `${formatNumber(user?.availableCp)} P`, color: "#8b5cf6" },
  ];

  return (
    <Layout>
      <div className="wallet-user-detail">
        <div className="d-flex justify-content-between align-items-center">
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <button
              onClick={() => navigate(-1)}
              style={{ width: '28px', height: '28px', padding: 0, fontSize: '18px', border: 'none', borderRadius: '6px', background: '#f4f4f5', color: '#374151', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, flexShrink: 0 }}
            >
              ←
            </button>
            <h4 style={{ fontWeight: 700, color: "#1f2937", margin: 0 }}>사용자 지갑 상세</h4>
          </div>
          {/* 권한 없으면 숨김 (R-FE-04) */}
          {mayManualCharge && targetUserId && (
            <div className="wud-action-bar">
              <button
                className="btn btn-primary btn-sm"
                onClick={() => setShowChargeModal(true)}
              >
                수기 충전
              </button>
            </div>
          )}
        </div>

        {/* 사용자 ID 검색 (paramUserId 없을 때) */}
        {!paramUserId && (
          <div className="wud-section">
            <div className="wud-section-title">사용자 검색</div>
            <div className="wud-search-bar">
              <input
                className="wud-search-input"
                type="text"
                placeholder="사용자 ID 입력"
                value={searchId}
                onChange={(e) => setSearchId(e.target.value)}
                onKeyDown={handleSearchKeyDown}
              />
              <button className="btn btn-primary btn-sm" onClick={handleSearch}>
                검색
              </button>
            </div>
          </div>
        )}

        {loadingUser && (
          <div className="text-center text-muted py-4">불러오는 중...</div>
        )}

        {error && (
          <div className="alert alert-danger py-2">{error}</div>
        )}

        {!loadingUser && user && (
          <>
            {/* 섹션 1: 사용자 기본 정보 */}
            <div className="wud-section">
              <div className="wud-section-title">사용자 기본 정보</div>
              <div className="wud-info-grid">
                <span className="wud-info-label">사용자명</span>
                <span className="wud-info-value">{user.userName || "-"}</span>
                <span className="wud-info-label">회원번호</span>
                <span className="wud-info-value">{user.memberNo || "-"}</span>
                <span className="wud-info-label">사용자 ID</span>
                <span className="wud-info-value">{user.userId || targetUserId}</span>
                <span className="wud-info-label">위험 상태</span>
                <span className="wud-info-value">
                  <RiskBadge status={user.riskStatus} />
                </span>
                {user.referralDebt > 0 && (
                  <>
                    <span className="wud-info-label">레퍼럴 부채</span>
                    <span className="wud-info-value wud-debt-highlight">
                      {formatNumber(user.referralDebt)} P
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* 섹션 2: 잔액 KPI 카드 그리드 */}
            <div className="wud-section">
              <div className="wud-section-title">잔액 현황</div>
              <div className="wud-kpi-grid">
                {kpiCards.map(({ label, value, color }) => (
                  <div key={label} className="wud-kpi-card" style={{ borderLeftColor: color }}>
                    <div className="wud-kpi-label">{label}</div>
                    <div className="wud-kpi-value">{value}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* 섹션 3: 최근 원장 이력 */}
            <div className="wud-section">
              <div className="wud-section-title">최근 원장 이력 (최대 20건)</div>
              {ledger.length === 0 ? (
                <div className="wud-empty">원장 이력이 없습니다</div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table className="wud-ledger-table">
                    <thead>
                      <tr>
                        <th>거래ID</th>
                        <th>유형</th>
                        <th>포인트유형</th>
                        <th>증감금액</th>
                        <th>처리일시</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledger.slice(0, 20).map((row, idx) => (
                        <tr key={row.ledgerId || idx}>
                          <td>{row.ledgerId ?? "-"}</td>
                          <td>{LEDGER_TYPE_LABEL[row.type] || row.type || "-"}</td>
                          <td>{row.pointType || "-"}</td>
                          <td>
                            {row.amount > 0 ? (
                              <span className="wud-ledger-amount-pos">+{formatNumber(row.amount)}</span>
                            ) : row.amount < 0 ? (
                              <span className="wud-ledger-amount-neg">{formatNumber(row.amount)}</span>
                            ) : (
                              <span>{formatNumber(row.amount)}</span>
                            )}
                          </td>
                          <td>{row.createdAt ? row.createdAt.slice(0, 10) : "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* 섹션 4: CS 메모 */}
            <div className="wud-section">
              <div className="wud-section-title">CS 메모</div>
              <div className="mb-2" style={{ paddingTop: '8px' }}>
                <textarea
                  className="form-control"
                  rows={3}
                  maxLength={500}
                  placeholder="메모 내용을 입력하세요 (최대 500자)"
                  value={memoContent}
                  onChange={(e) => setMemoContent(e.target.value)}
                  style={{ fontSize: "0.88rem", resize: "none" }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginTop: '6px' }}>
                  <span style={{ fontSize: "0.75rem", color: "#aaa" }}>
                    {memoContent.length}/500
                  </span>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={handleMemoSubmit}
                    disabled={memoSubmitting || !memoContent.trim()}
                  >
                    {memoSubmitting ? "등록 중..." : "등록"}
                  </button>
                </div>
              </div>
              {memos.length === 0 ? (
                <div className="wud-empty">등록된 메모가 없습니다</div>
              ) : (
                <div className="wud-memo-list">
                  {memos.slice(0, 10).map((memo, idx) => (
                    <div key={memo.id || idx} className="wud-memo-item">
                      <div className="wud-memo-content">{memo.content}</div>
                      <div className="wud-memo-date">{formatDateTime(memo.createdAt)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 섹션 5: 위험 상태 설정 — 권한 없으면 숨김 (R-FE-04) */}
            {mayUpdateRiskFlag && (
            <div className="wud-section">
              <div className="wud-section-title">위험 상태 설정</div>
              <div className="mb-2">
                <span style={{ fontSize: "0.88rem", color: "#555", marginRight: 8 }}>현재 상태:</span>
                <RiskBadge status={user.riskStatus} />
              </div>
              <div className="d-flex flex-column gap-2" style={{ maxWidth: 400 }}>
                <select
                  className="form-select form-select-sm"
                  value={riskStatus}
                  onChange={(e) => setRiskStatus(e.target.value)}
                >
                  <option value="">상태 선택</option>
                  <option value="NORMAL">NORMAL (정상)</option>
                  <option value="CAUTION">CAUTION (주의)</option>
                  <option value="RESTRICTED">RESTRICTED (제한)</option>
                </select>
                <textarea
                  className="form-control"
                  rows={2}
                  placeholder="변경 사유를 입력하세요"
                  value={riskReason}
                  onChange={(e) => setRiskReason(e.target.value)}
                  style={{ fontSize: "0.88rem", resize: "none" }}
                />
                <div>
                  <button
                    className="btn btn-warning btn-sm"
                    onClick={handleRiskSubmit}
                    disabled={riskSubmitting || !riskStatus || !riskReason.trim()}
                  >
                    {riskSubmitting ? "변경 중..." : "위험 상태 변경"}
                  </button>
                </div>
              </div>
            </div>
            )}
          </>
        )}

        {/* 수기 충전 모달 */}
        <ManualChargeModal
          isOpen={showChargeModal}
          userId={targetUserId}
          userDetail={user}
          onClose={() => setShowChargeModal(false)}
          onSuccess={() => {
            setShowChargeModal(false);
            setRefreshKey((k) => k + 1);
          }}
        />
      </div>
    </Layout>
  );
}

export default WalletUserDetail;
